import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useEffect
} from "react";
import { createPortal } from "react-dom";
import { IActionsheetRefProps, ActionsheetProps } from "./types";

export const ActionSheet = forwardRef<IActionsheetRefProps, ActionsheetProps>(
  function ActionSheet({ children, backdropCanClose = true }, ref) {
    const [isOpen, setIsOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    useImperativeHandle(
      ref,
      (): IActionsheetRefProps => ({
        open: () => {
          setIsOpen(true);
          // Use requestAnimationFrame to ensure DOM is updated before animation
          requestAnimationFrame(() => {
            setIsAnimating(true);
          });
          // Prevent body scroll when modal is open
          document.body.style.overflow = "hidden";
        },
        close: () => {
          setIsAnimating(false);
          // Small delay to allow animation to complete
          setTimeout(() => {
            setIsOpen(false);
            document.body.style.overflow = "";
          }, 300);
        }
      }),
      []
    );

    const handleClose = useCallback(() => {
      if (backdropCanClose) {
        setIsAnimating(false);
        setTimeout(() => {
          setIsOpen(false);
          document.body.style.overflow = "";
        }, 300);
      }
    }, [backdropCanClose]);

    const handleBackdropClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && backdropCanClose) {
          handleClose();
        }
      },
      [backdropCanClose, handleClose]
    );

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        document.body.style.overflow = "";
      };
    }, []);

    if (!isOpen) {
      return null;
    }

    const content = (
      <div
        onClick={handleBackdropClick}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          opacity: isAnimating ? 1 : 0,
          transition: "opacity 0.3s ease-in-out"
        }}
      >
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
            width: "100%",
            maxWidth: "448px",
            maxHeight: "600px",
            overflowY: "auto",
            boxShadow: "0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)",
            transform: isAnimating ? "translateY(0)" : "translateY(100%)",
            transition: "transform 0.3s ease-out"
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Handle indicator */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              paddingTop: "12px",
              paddingBottom: "8px"
            }}
          >
            <div
              style={{
                backgroundColor: "#D1D5DB",
                borderRadius: "9999px",
                width: "40px",
                height: "4px"
              }}
            />
          </div>

          {/* Content */}
          <div
            style={{
              paddingLeft: "8px",
              paddingRight: "8px",
              paddingBottom: "8px"
            }}
          >
            {children}
          </div>
        </div>
      </div>
    );

    // Render to portal to ensure it's on top of everything
    return typeof document !== "undefined"
      ? createPortal(content, document.body)
      : null;
  }
);
